import { Avatar, Button, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useDictionary, useIsDesktop, useIsWebView } from 'Hooks';
import { UserInfo, Header2, Footer, BurgerMenu, CookieConsentPopup } from 'Components';
import { BGDesktop, BGNewMobile, FamilyMembersIntro, WvBg } from 'Images';
import { B2CGuard } from 'Components/B2CGuard';
import { useScrollTopView } from 'Hooks/useScrollTopView';
import { useRef, useState } from 'react';
import { useFocusFirstPossibleElement } from 'Hooks/useFocusFirstPossibleElement';
import { CustomDialog } from 'Components/CustomDialog';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useShowUserInfo } from 'Hooks/useShowUserInfo';
import { Feedback } from 'Components/Feedback';
import { ROUTES } from 'Config';

const WEBVIEW_BACKGROUND_COLOR = '#FAFBFE';

export const PrivateLayout = () => {
  const [showModal, setShowModal] = useState<boolean>(false);

  useScrollTopView({ smoothScroll: true });

  const mainContent = useRef<HTMLDivElement | null>(null);

  const isDesktop = useIsDesktop();
  const isWebView = useIsWebView();
  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));
  const dictionary = useDictionary(Dictionary, DICTIONARY.Lawyers);

  useFocusFirstPossibleElement(mainContent.current);

  const showUserInfo = useShowUserInfo();

  const showFeedback: boolean =
    !window.location.pathname.includes(ROUTES.ADMIN_FEEDBACK) &&
    !window.location.pathname.includes(ROUTES.EMERGENCY_CALL) &&
    window.location.pathname !== ROUTES.REIMBURSEMENTS &&
    window.location.pathname !== `${ROUTES.REIMBURSEMENTS}/` &&
    !window.location.pathname.includes(ROUTES.DATA_ANALYSIS);

  return (
    <B2CGuard>
      <Header2 />
      <Stack
        sx={
          isWebView
            ? {
                position: 'relative',
                bgcolor: WEBVIEW_BACKGROUND_COLOR,
                backgroundImage: `url(${WvBg})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top center',
                backgroundSize: '100% auto',
              }
            : { bgcolor: '#fff' }
        }
      >
        {!isWebView && (
          <Avatar
            src={isDesktop ? BGDesktop : BGNewMobile}
            alt={''}
            sx={{
              width: isDesktop && isScreenXl ? 'calc(100vw - 300px)' : 'auto',
              height: isDesktop ? '660px' : '335px',
              position: 'absolute',
              top: '72px',
              left: 0,
              right: 0,
            }}
          />
        )}

        <Container component={'main'}>
          <Stack
            direction={'row'}
            sx={{
              minHeight: isWebView ? '100vh' : 'calc(100vh - 72px - 100px)',
            }}
            ref={mainContent}
          >
            <BurgerMenu />
            <Stack>
              <Stack
                tabIndex={-1}
                sx={{
                  paddingTop: { sm: '28px', xs: '20px' },
                  paddingBottom: { sm: '80px', xs: '65px' },
                  paddingRight: { lg: '65px', sm: '24px', xs: '0' },
                  paddingLeft: { sm: '24px', xs: '0' },
                }}
              >
                {showUserInfo && <UserInfo />}
                <Outlet />
              </Stack>
              {showFeedback && <Feedback />}
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Footer />
      <CustomDialog
        onclose={() => {
          setShowModal(false);
        }}
        showDialog={showModal}
        width={isDesktop ? '600px' : 'calc(100vw - 50px)'}
        height={isDesktop ? '540px' : '400px'}
      >
        <Stack
          sx={{
            alignItems: 'center',
          }}
        >
          <Avatar alt={''} src={FamilyMembersIntro} sx={{ width: '142px', height: '97px', mt: '64px' }} />
          <Typography
            sx={{
              fontSize: '32px !important',
              fontWeight: 700,
              lineHeight: '40px',
              mt: '24px',
            }}
            component='h1'
          >
            {dictionary.didNotSelect}
          </Typography>
          <Typography
            sx={{
              fontSize: '20px !important',
              fontWeight: '400 !important',
              lineHeight: '26px',
              textAlign: 'center',
              mt: '12px',
              maxWidth: '34ch',
            }}
          >
            {dictionary.goBackToList}
          </Typography>
          <Button sx={{ mt: '56px' }} onClick={() => setShowModal(false)} variant='contained'>
            {dictionary.backToList}
          </Button>
        </Stack>
      </CustomDialog>
      <CookieConsentPopup />
    </B2CGuard>
  );
};
